/**
 * Runtime feature flag: "show in-progress (incomplete-data) sections".
 *
 * Sections backed by data we can't fully derive yet are HIDDEN by default. Setting
 * the `show_in_progress` localStorage key to `'true'` reveals them. The flag is read
 * at RENDER time (never baked in at app init), so it can be flipped at any point:
 *   - In devtools:  localStorage.setItem('show_in_progress', 'true')  → then reload
 *     this tab (or it updates live in any OTHER open tab via the `storage` event).
 *   - In-app / console:  setShowInProgress(true)  → updates live in the current tab
 *     too (it dispatches a same-tab change event).
 *
 * Shared nova hook: consumed by Pulse (in-progress cells / the VS. Network section)
 * and Stats (the still-mock Top Games board). Gate any section whose backing data
 * isn't available yet behind this flag rather than shipping placeholder numbers.
 */
import React from 'react';

export const SHOW_IN_PROGRESS_KEY = 'show_in_progress';
/**
 * Same-tab change notification — `storage` only fires in OTHER tabs/windows.
 *
 * Exported because it is the reveal CHANNEL, not this flag's private business:
 * `config/featureFlags` has per-feature reveal keys of its own (the "777creatives"
 * kind) and they have to arrive the same way, or a flag flips in storage while
 * every subscribed surface carries on rendering the old answer.
 */
export const REVEAL_CHANGE_EVENT = 'ows:show-in-progress-changed';
const CHANGE_EVENT = REVEAL_CHANGE_EVENT;

/** Subscribe to any reveal flag changing, in this tab or another. Shared with
 *  {@link useFeatureGate}, which watches more keys than this module owns. */
export const subscribeReveal = (onChange: () => void): (() => void) => {
  window.addEventListener('storage', onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
};

const read = (): boolean => {
  try {
    return window.localStorage.getItem(SHOW_IN_PROGRESS_KEY) === 'true';
  } catch {
    // Private mode / blocked storage → default to hidden.
    return false;
  }
};

const subscribe = (onChange: () => void): (() => void) => {
  window.addEventListener('storage', onChange);
  window.addEventListener(CHANGE_EVENT, onChange);
  return () => {
    window.removeEventListener('storage', onChange);
    window.removeEventListener(CHANGE_EVENT, onChange);
  };
};

/**
 * Whether in-progress sections should render. Reactive: re-renders when the flag is
 * toggled via {@link setShowInProgress} (same tab) or changed in another tab.
 */
export const useShowInProgress = (): boolean => React.useSyncExternalStore(subscribe, read, () => false);

/** Read the flag's current value imperatively (non-reactive) — for event handlers
 * that need to TOGGLE it (e.g. the Lumio "777" easter egg) without subscribing. */
export const getShowInProgress = (): boolean => read();

/** Imperatively flip the flag (handy from the console) and notify the current tab. */
export const setShowInProgress = (on: boolean): void => {
  try {
    window.localStorage.setItem(SHOW_IN_PROGRESS_KEY, on ? 'true' : 'false');
    window.dispatchEvent(new Event(CHANGE_EVENT));
  } catch {
    // ignore unavailable storage
  }
};

export default useShowInProgress;
